import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    styled,
    Card,
    Grid,
    Button,
    FormLabel,
    FormControl,
    Divider,
    Typography,
    Table,
    TableContainer,
    TableBody,
    AccordionDetails,
    AccordionSummary,
    Accordion,
    useTheme,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import type { TooltipProps } from "@mui/material/Tooltip";

import {
    BadgeOutlined,
    AlternateEmailOutlined,
    LocationOnOutlined,
    ChevronRightOutlined,
    Celebration,
    Event,
    ExpandMore,
    Close,
} from "@mui/icons-material";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";

import { AccountModal, Block } from "./accountPieces";
import FlexBox from "./components/FlexBox";
import { AccountHeader, CapHeader, ClickableText, H6, Paragraph, Span, Tiny } from "./components/Typography";
import EditImg from "../images/edit.png";
import LIAccountDataRow from "./components/LIAccountDataRow";
import { Axios, buttonGradient, dateFormat, formatMoney, navigateToTop, topSeparatorMargin } from "../../common/utils";
import { baseApi } from "../../common/constants";
import { AccountInputBox, AddressLookup } from "./components/TableActions";
import { fieldRules } from "../../common/commonComps";
import ShoppingTableRow from "./ShoppingComponents/ShoppingTableRow";
import TableRowMobile from "./components/TableRowMobile";
import LITableHead from "./components/LITableHead";
import {
    AccountWrap,
    DollarTitles,
    EarningBox,
    GradientTop,
    ModalBox,
    ModalButtonWrap,
    SecondaryButton,
    shoppingTableHeadCells,
} from "./components/pieces";
import Details from "./Details";
import { accountInputBoxCSS } from "../subComps/loginPieces";
import Ad from "../ad";
import RequestCashBack from "./ShoppingComponents/RequestCashBack";
import type { Size, User, AppContext, Config, Purchase } from "../../types";
import type { ValidationError } from "yup";

// cbData carries dynamic form-field keys plus method/email/phone/cashback/offset,
// so it is typed as an open record (matching the RequestCashBack prop it is passed to).
type CbDataRecord = Record<string, string | number | null | undefined>;

const InputBox = styled(AccountInputBox)(accountInputBoxCSS);

const AddressBox = styled(AddressLookup)(accountInputBoxCSS);

const hw = { height: `30px`, width: `30px` };

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: `#f5f5f9`,
        color: `rgba(0, 0, 0, 0.87)`,
        fontSize: theme.typography.pxToRem(12),
        border: `1px solid #dadde9`,
    },
}));

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

interface MyAccountProps {
    user: User;
    refreshContext: () => Promise<void>;
    context: AppContext;
    useMobileTables: boolean;
    setContext: (nv: Partial<Config>) => void;
    editable: boolean;
}

const MyAccount = ({ user, refreshContext, context, useMobileTables, setContext, editable }: MyAccountProps) => {
    const [size] = useOutletContext<[Size, (open?: unknown) => void]>();
    const navigate = useNavigate();
    const theme = useTheme();
    // member_portal's shared User type omits address1/address2, which exist on the record at runtime.
    const userWithAddress = user as User & { address1?: string; address2?: string };
    const [invoices, setInvoices] = useState<(Purchase & { i: number })[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<Record<string, Record<string, string>>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [lookupOn, setLookupOn] = useState(false);
    const [cbData, setCBData] = useState<CbDataRecord | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [editingText, setEditingText] = useState(false);
    const showDetails = searchParams.get(`showDetails`);
    const sectionHeaders = context?.config.sectionHeaders || {};
    const textBlocks = context?.config.textBlocks || {};
    const closeDetails = () => setSearchParams({});
    const questions = context?.config.faq || [];

    const editingKey = useMemo(() => Object.keys(editing)[0], [editing]);

    const clearEditing = () => setEditing({});

    const closeOnEsc = (e: KeyboardEvent) => {
        if (e.key === `Escape`) {
            clearEditing();
            window.focus();
        }
    };

    const gridRef = useRef<HTMLDivElement>(null);
    const gridLarge = (gridRef?.current?.clientWidth as number) > 816;
    const ahRef = useRef<HTMLElement>(null);
    const ahSubRef = useRef<HTMLElement>(null);
    const pillRef = useRef<HTMLElement>(null);
    const pendingRef = useRef<HTMLElement>(null);

    useEffect(() => {
        document.body.removeEventListener(`keyup`, closeOnEsc);
        document.body.addEventListener(`keyup`, closeOnEsc);

        Axios.get(`${baseApi}shoppingtrips?mode=year&limit=6&includepayouts=1`)
            .then(r => r.data)
            .then(d => {
                setInvoices(d.results.map((x: Purchase, i: number) => ({ i, ...x })) || []);
                setLoading(false);
            });

        return () => document.body.removeEventListener(`keyup`, closeOnEsc);
    }, []);

    const setEdit = (v: Record<string, Record<string, string>>) => {
        setErrors({});
        setEditing(v);
    };

    const saveInfo = () => {
        const data = { ...editing[editingKey] };
        setErrors({});
        setLoading(true);
        fieldRules[editingKey].fields.forEach(f => {
            if (f.regex) {
                data[f.v] = (data[f.v] || ``).replace(f.regex, ``);
            }
        });
        fieldRules[editingKey].validator
            .validate(data)
            .then(() =>
                Axios.put(`${baseApi}usersetting`, {
                    [editingKey]: data,
                })
            )

            .then(() => {
                setLoading(false);

                return refreshContext().then(clearEditing);
            })
            .catch((e: ValidationError) => {
                setLoading(false);
                // Original spread `...errors.editingKey`, but that key is never set, so it always spread
                // `undefined` (a no-op); the raw-string else branch is only read via field-name keys, so an
                // equivalent single-key object is used.
                setErrors(e.params?.path ? { [e.params.path as string]: e.message } : { _form: e.message });
            });
    };

    return (
        <Block size={size}>
            <RequestCashBack {...{ cbData, setCBData, size, context, refreshContext }} />
            <AccountWrap size={size}>
                {!!showDetails && <Details {...{ showDetails, size, closeDetails }} />}
                <AccountHeader
                    size={size}
                    ref={ahRef}
                    contentEditable={editingText}
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: sectionHeaders.account || `Cash Back Account` }}
                    onBlur={v => {
                        setContext({ sectionHeaders: { ...sectionHeaders, account: v.target.innerHTML } });
                        setEditingText(false);
                    }}
                    {...(editable && !editingText
                        ? {
                              onClick: () => {
                                  setEditingText(true);
                                  setTimeout(() => {
                                      ahRef.current!.focus();
                                  }, 0);
                              },
                          }
                        : {})}
                    className={editingText ? `editingText` : editable ? `edit` : ``}
                />
                <Card
                    sx={{
                        borderRadius: `12px 12px 0 0`,
                        px: 3,
                        pb: size === `large` ? 3 : 0,
                        background: theme.palette.account.background,
                    }}
                >
                    <CapHeader
                        size={size}
                        py={3}
                        m={0}
                        ref={ahSubRef}
                        contentEditable={editingText}
                        suppressContentEditableWarning
                        dangerouslySetInnerHTML={{ __html: sectionHeaders.accountSub || `AVAILABLE CASH BACK` }}
                        onBlur={v => {
                            setContext({ sectionHeaders: { ...sectionHeaders, accountSub: v.target.innerHTML } });
                            setEditingText(false);
                        }}
                        {...(editable && !editingText
                            ? {
                                  onClick: () => {
                                      setEditingText(true);
                                      setTimeout(() => {
                                          ahSubRef.current!.focus();
                                      }, 0);
                                  },
                              }
                            : {})}
                        className={editingText ? `editingText` : editable ? `edit` : ``}
                    />
                    <Grid ref={gridRef} container spacing="5px">
                        {/* Grid uses pre-v7 item/xs/sm/md props, forwarded via spread to preserve the original runtime props. */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <EarningBox box="payout">
                                <DollarTitles sx={{ flex: `1 0 99%` }} my={0.5}>
                                    {formatMoney(user.lifetimeAmt as number)}
                                </DollarTitles>
                                <Tiny sx={{ flex: `1 0 99%`, margin: 0 }} fontWeight={500}>
                                    Lifetime Payouts
                                </Tiny>
                                <Divider
                                    sx={{
                                        width: `100%`,
                                        margin: `16px 0`,
                                        color: theme.palette.account.whiteboxBorder,
                                    }}
                                />
                                <Box sx={{ display: `flex`, justifyContent: `flex-end`, width: `100%` }}>
                                    <ClickableText
                                        onClick={() => navigate(`/payouts`)}
                                        sx={{
                                            cursor: `pointer`,
                                            marginRight: `-5px`,
                                            color: theme.palette.account.accent,
                                        }}
                                    >
                                        See{size !== `small` ? ` Payout ` : ` `}History
                                        <ChevronRightOutlined sx={{ verticalAlign: `middle` }} />
                                    </ClickableText>
                                </Box>
                            </EarningBox>
                        </Grid>
                        {/* Grid uses pre-v7 item/xs/sm/md props, forwarded via spread to preserve the original runtime props. */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <EarningBox box="balance">
                                <DollarTitles sx={{ flex: `1 0 99%` }} my={0.5}>
                                    {formatMoney(user.redeemableAmt as number)}
                                </DollarTitles>
                                <Tiny
                                    // Tiny is typed StyledTextProps (which omits size) though it consumes size; forwarded via spread.
                                    {...{ size }}
                                    sx={{ flex: `1 0 99%`, margin: 0, fontWeight: 500 }}
                                    ref={pillRef}
                                    contentEditable={editingText}
                                    suppressContentEditableWarning
                                    dangerouslySetInnerHTML={{
                                        __html: sectionHeaders.pillCB || `Available Cash Back`,
                                    }}
                                    onBlur={v => {
                                        setContext({
                                            sectionHeaders: { ...sectionHeaders, pillCB: v.target.innerHTML },
                                        });
                                        setEditingText(false);
                                    }}
                                    {...(editable && !editingText
                                        ? {
                                              onClick: () => {
                                                  setEditingText(true);
                                                  setTimeout(() => {
                                                      pillRef.current!.focus();
                                                  }, 0);
                                              },
                                          }
                                        : {})}
                                    className={editingText ? `editingText` : editable ? `edit` : ``}
                                />
                                <Divider sx={{ width: `100%`, margin: `16px 0`, color: `#E1E1E1` }} />
                                <Box
                                    sx={{
                                        display: `flex`,
                                        justifyContent: `space-between`,
                                        width: `100%`,
                                        flexWrap: `wrap`,
                                    }}
                                >
                                    <Span
                                        sx={{
                                            margin: `2px 0 0`,
                                            fontSize: 15,
                                            fontWeight: 500,
                                            height: `20px`,
                                            cursor: `help`,
                                        }}
                                    >
                                        <HtmlTooltip
                                            enterTouchDelay={0}
                                            leaveTouchDelay={10000}
                                            disableFocusListener
                                            placement="top-end"
                                            title={
                                                <Box p={3} sx={{ maxWidth: 400 }}>
                                                    <Typography sx={{ fontSize: 16, fontWeight: 600 }} color="inherit">
                                                        Pending Balance
                                                    </Typography>
                                                    <Box
                                                        ref={pendingRef}
                                                        contentEditable={editingText}
                                                        suppressContentEditableWarning
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                textBlocks.pendingPopover ||
                                                                `Your cash back begins as Pending to allow time for you to make
                                                        returns or exchanges. When the return window closes (which
                                                        varies by merchant but is often 30-90 days) the cash back status
                                                        will change from Pending to Confirmed.`,
                                                        }}
                                                        onBlur={v => {
                                                            setContext({
                                                                textBlocks: {
                                                                    ...textBlocks,
                                                                    pendingPopover: v.target.innerHTML,
                                                                },
                                                            });
                                                            setEditingText(false);
                                                        }}
                                                        {...(editable && !editingText
                                                            ? {
                                                                  onClick: () => {
                                                                      setEditingText(true);
                                                                      setTimeout(() => {
                                                                          pendingRef.current!.focus();
                                                                      }, 0);
                                                                  },
                                                              }
                                                            : {})}
                                                        className={editingText ? `editingText` : editable ? `edit` : ``}
                                                        sx={{
                                                            fontSize: 14,
                                                            fontWeight: 400,

                                                            "&.edit": {
                                                                cursor: `url(${EditImg}), auto`,
                                                            },
                                                            "&.editing": {
                                                                cursor: `text`,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            }
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    borderBottom: `2px dotted ${theme.palette.account.accent}`,
                                                    position: `relative`,
                                                    marginTop: `3px`,
                                                }}
                                            >
                                                Pending
                                            </Box>
                                        </HtmlTooltip>
                                        :&nbsp;
                                        <Span>
                                            {formatMoney((user.currentAmt as number) - (user.redeemableAmt as number))}
                                        </Span>
                                    </Span>
                                    <ClickableText
                                        onClick={() => navigate(`/shopping`)}
                                        sx={{
                                            cursor: `pointer`,
                                            marginRight: `-5px`,
                                            marginLeft: size === `small` ? 0 : `5px`,
                                            color: theme.palette.account.accent,
                                        }}
                                    >
                                        See Activity
                                        <ChevronRightOutlined sx={{ verticalAlign: `middle` }} />
                                    </ClickableText>
                                </Box>
                            </EarningBox>
                        </Grid>
                        {/* Grid uses pre-v7 item/xs/sm/md props, forwarded via spread to preserve the original runtime props. */}
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <Box
                                sx={{
                                    paddingTop: 0,
                                    display: `flex`,
                                    height: `100%`,
                                    justifyContent: `space-around`,
                                    flexDirection: gridLarge && size === `large` ? `column` : `row`,
                                    flexWrap: `nowrap`,
                                    alignItems: gridLarge && size === `large` ? `flex-start` : `space-around`,
                                }}
                                px={3}
                                pt={gridLarge && size === `large` ? 3 : `5px`}
                            >
                                {user.nextPayoutSet || user.lastPayout ? (
                                    <FlexBox my={1}>
                                        {user.nextPayoutSet ? (
                                            <Event sx={{ color: `#333`, ...hw }} />
                                        ) : (
                                            <Celebration sx={{ color: `#333`, ...hw }} />
                                        )}
                                        <Box ml={2}>
                                            <H6 m={0}>{user.nextPayoutSet ? `Next` : `Last`} Payout</H6>
                                            <Tiny fontWeight={500} mt={0.3} m={0}>
                                                {dateFormat(user.nextPayoutSet ? context.nextFifteen : user.lastPayout)}
                                            </Tiny>
                                        </Box>
                                    </FlexBox>
                                ) : null}
                                <FlexBox my={1}>
                                    {(context?.adminConfig!.mall!.CORE_SETTINGS![`Payout Method`] as string).startsWith(
                                        `Forced`
                                    ) &&
                                        user.lastPayout && (
                                            <>
                                                <Event sx={{ color: `#333`, ...hw }} />
                                                <Box ml={2}>
                                                    <H6 m={0}>Next Payout</H6>
                                                    <Tiny fontWeight={500} mt={0.3} m={0}>
                                                        {dateFormat(context.nextFifteen)}
                                                    </Tiny>
                                                </Box>
                                            </>
                                        )}
                                    {context?.adminConfig!.mall!.CORE_SETTINGS![`Payout Method`] === `Request` && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => setCBData({})} // dont set keys in here
                                            sx={{
                                                background: buttonGradient(theme),
                                                padding: `4px 8px`,
                                                minWidth: `unset`,
                                                maxHeight: 40,
                                                borderRadius: 2,
                                                "&.Mui-disabled": {
                                                    background: `#999 !important`,
                                                    opacity: 0.5,
                                                },
                                            }}
                                        >
                                            Request Payout
                                        </Button>
                                    )}
                                </FlexBox>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
                {size !== `large` && context?.adminConfig?.mall?.ad_placements?.account_placement && (
                    <Box my={topSeparatorMargin(size)} mx="auto" sx={{ textAlign: `center` }}>
                        <Ad
                            {...{
                                ...context?.adminConfig?.mall?.ad_placements?.account_placement,
                                size,
                            }}
                        />
                    </Box>
                )}

                {context?.adminConfig?.mall?.show_my_info && (
                    <Card sx={{ mt: 3, px: 3, pb: 3, background: theme.palette.account.background }}>
                        <CapHeader py={3} m={0}>
                            MY INFO
                        </CapHeader>
                        <Box>
                            <LIAccountDataRow>
                                <FlexBox alignItems="center">
                                    <BadgeOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                    <Box ml={2}>
                                        <H6 m={0}>Name</H6>
                                        <Tiny fontWeight={400} mt={0.3} m={0}>
                                            {user.firstName || user.lastName
                                                ? `${(user.firstName ?? ``).toLowerCase()} ${(
                                                      user.lastName ?? ``
                                                  ).toLowerCase()}`
                                                : `No name found.`}
                                        </Tiny>
                                    </Box>
                                </FlexBox>
                                {context?.adminConfig?.mall?.can_edit_name && (
                                    <ClickableText
                                        sx={{ pointerEvents: `all` }}
                                        onClick={() =>
                                            setEdit({
                                                name: {
                                                    firstName: user.firstName || ``,
                                                    lastName: user.lastName || ``,
                                                },
                                            })
                                        }
                                    >
                                        Edit
                                    </ClickableText>
                                )}
                            </LIAccountDataRow>
                            <LIAccountDataRow>
                                <FlexBox alignItems="center">
                                    <AlternateEmailOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                    <Box ml={2}>
                                        <H6 m={0}>Email</H6>
                                        <Tiny fontWeight={400} mt={0.3} m={0}>
                                            {user.email}
                                        </Tiny>
                                    </Box>
                                </FlexBox>
                            </LIAccountDataRow>
                            <LIAccountDataRow>
                                <FlexBox alignItems="center">
                                    <LocationOnOutlined sx={{ color: theme.palette.account.primary, ...hw }} />
                                    <Box ml={2}>
                                        <H6 m={0}>Address</H6>
                                        {!userWithAddress.address1 && (
                                            <Tiny fontWeight={400} mt={0.3} m={0}>
                                                No address found.
                                            </Tiny>
                                        )}
                                        {userWithAddress.address1 && (
                                            <Tiny fontWeight={400} mt={0.3} m={0}>
                                                {userWithAddress.address1 || ``}
                                                {userWithAddress.address2 ? `, ${userWithAddress.address2}.` : `.`}{" "}
                                                {user.city}, {user.state}
                                                {` `}
                                                {user.zipcode}
                                            </Tiny>
                                        )}
                                    </Box>
                                </FlexBox>
                                {context?.adminConfig?.mall?.can_edit_address && (
                                    <ClickableText
                                        sx={{ pointerEvents: `all` }}
                                        onClick={() =>
                                            setEdit({
                                                address: {
                                                    address1: userWithAddress.address1 || ``,
                                                    address2: userWithAddress.address2 || ``,
                                                    city: user.city || ``,
                                                    state: user.state || ``,
                                                    zipcode: user.zipcode || ``,
                                                },
                                            })
                                        }
                                    >
                                        Edit
                                    </ClickableText>
                                )}
                            </LIAccountDataRow>
                        </Box>
                    </Card>
                )}

                {!!invoices?.length && (
                    <Card
                        sx={{
                            px: 3,
                            mt: topSeparatorMargin(size),
                            pb: 1.5,
                            background: theme.palette.account.background,
                        }}
                    >
                        <CapHeader size={size} py={3} m={0}>
                            RECENT ACTIVITY
                        </CapHeader>
                        <TableContainer sx={{ borderRadius: `16px`, border: `1px solid #E1E1E1`, overflow: `hidden` }}>
                            <Table
                                sx={{
                                    border: `none`,
                                }}
                            >
                                <LITableHead
                                    {...{ useMobileTables, editable, context, setContext }}
                                    headCells={shoppingTableHeadCells}
                                />
                                <TableBody sx={{ background: `#FFF` }}>
                                    {invoices.map(purchase =>
                                        useMobileTables ? (
                                            <TableRowMobile
                                                onClick={() =>
                                                    setSearchParams({
                                                        showDetails: encodeURIComponent(purchase.invoiceId),
                                                    })
                                                }
                                                key={purchase.i}
                                                purchase={purchase}
                                            />
                                        ) : (
                                            <ShoppingTableRow key={purchase.i} {...{ purchase, setSearchParams }} />
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ textAlign: `center`, padding: `12px 0 0` }}>
                            <ClickableText
                                onClick={() => navigateToTop(navigate, `/shopping`)}
                                sx={{
                                    cursor: `pointer`,
                                    marginRight: `-5px`,
                                    marginLeft: `5px`,
                                    marginTop: `10px`,
                                    marginBottom: `10px`,
                                    color: theme.palette.account.accent,
                                }}
                            >
                                See All Activity
                                <ChevronRightOutlined sx={{ verticalAlign: `middle` }} />
                            </ClickableText>
                        </Box>
                    </Card>
                )}

                {!!questions.length && (
                    <Card
                        sx={{
                            borderRadius: `0 0 12px 12px`,
                            px: 3,
                            mt: topSeparatorMargin(size),
                            pb: 3,
                            background: theme.palette.account.background,
                        }}
                    >
                        <CapHeader size={size} py={3} m={0}>
                            FREQUENTLY ASKED
                        </CapHeader>
                        <Box
                            sx={{
                                borderRadius: `16px`,
                                border: `1px solid #E1E1E1`,
                                overflow: `hidden`,
                                background: `#FFF`,
                            }}
                        >
                            {questions.map(q => (
                                <Accordion
                                    key={q.q}
                                    sx={{
                                        "&:before": {
                                            display: `none`,
                                        },
                                        borderBottom: `1px solid #E1E1E1`,
                                        "&:last-of-type": {
                                            borderBottom: `none`,
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls="panel1-content"
                                        id="panel1-header"
                                    >
                                        <Typography
                                            dangerouslySetInnerHTML={{
                                                __html: q.q,
                                            }}
                                            sx={{ fontWeight: 600 }}
                                            component="span"
                                        />
                                    </AccordionSummary>
                                    <AccordionDetails
                                        dangerouslySetInnerHTML={{
                                            __html: q.a,
                                        }}
                                    />
                                </Accordion>
                            ))}
                        </Box>
                    </Card>
                )}

                {!!editingKey && (
                    <AccountModal onClose={clearEditing} size={size} open={!!editingKey}>
                        <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                            <Close
                                sx={{
                                    position: `absolute`,
                                    top: 24,
                                    right: 10,
                                    zIndex: 30,
                                    cursor: `pointer`,
                                    opacity: 0.55,
                                    height: 25,
                                    width: 25,
                                    zindex: 30,
                                }}
                                onClick={() => setEdit({})}
                            />
                            {size !== `small` && <GradientTop />}
                            <ModalBox>
                                {size === `small` && (
                                    <Box p={2} sx={{ textAlign: `center` }}>
                                        <Box
                                            component="img"
                                            sx={{ width: 150 }}
                                            src={context?.adminConfig?.mall?.Color_Desktop_Logo}
                                        />
                                    </Box>
                                )}
                                <Heading>{fieldRules[editingKey].title}</Heading>
                                <Box>
                                    {fieldRules[editingKey].fields.map(e => (
                                        <FormControl
                                            key={e.l}
                                            sx={{
                                                width: `100%`,
                                                mr: e.mr,
                                                maxWidth: e.width,
                                                "& .MuiFormLabel-root": {
                                                    color: `#666`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    justifyContent: `space-between`,
                                                    ...(e.hidden ? { display: `none` } : {}),
                                                }}
                                            >
                                                <FormLabel
                                                    sx={{ fontSize: 16, fontWeight: 600, color: `#333` }}
                                                    htmlFor={e.l}
                                                >
                                                    {e.l}
                                                </FormLabel>
                                            </Box>
                                            {e.v === `address1` ? (
                                                <AddressBox
                                                    name={e.l}
                                                    type={e.t}
                                                    resetDirty
                                                    // original passes the lowercase `autocomplete` prop; forwarded via spread to preserve it.
                                                    {...{ autocomplete: e.ac }}
                                                    sendResponse={v => {
                                                        // AddressResponse has no index signature; spread into a plain
                                                        // string record (all fields are strings) to match setEdit's shape.
                                                        setEdit({ address: { ...v } });
                                                    }}
                                                    id={e.l}
                                                    disabled={loading || !!e.disabled}
                                                    onClick={() =>
                                                        setEdit({
                                                            [editingKey]: {},
                                                        })
                                                    }
                                                    fullWidth
                                                    InputProps={{
                                                        sx: {
                                                            background: `#FFF`,
                                                            marginTop: `5px`,
                                                            fontWeight: 500,
                                                            paddingRight: 0,
                                                        },
                                                    }}
                                                    sx={{
                                                        mb: 3,
                                                        maxWidth: `unset`,
                                                        ...(e.hidden ? { display: `none` } : {}),
                                                        input: { fontWeight: 500 },
                                                    }}
                                                    currentValue={editing[editingKey][e.v]}
                                                    helperText={errors[e.v]}
                                                    error={!!errors[e.v]}
                                                    lookupOn={lookupOn}
                                                    onChange={v => {
                                                        const val = v.target.value;
                                                        if (val) {
                                                            if (
                                                                Math.abs(
                                                                    // editing[e.v] is a top-level lookup of a field-name key
                                                                    // that is never set, so it is undefined at runtime; String()
                                                                    // coercion keeps the original (`?? ``).length` == 0 result.
                                                                    String(editing[e.v] ?? ``).length -
                                                                        (val ?? ``).length
                                                                ) < 3
                                                            ) {
                                                                setLookupOn(true);
                                                            }
                                                        } else {
                                                            setEdit({
                                                                [editingKey]: {},
                                                            });
                                                            setLookupOn(false);
                                                            return;
                                                        }
                                                        setEdit({
                                                            [editingKey]: {
                                                                ...editing[editingKey],
                                                                [e.v]: e.visualFilter
                                                                    ? e.visualFilter(v.target.value)
                                                                    : v.target.value,
                                                            },
                                                        });
                                                    }}
                                                />
                                            ) : (
                                                <InputBox
                                                    name={e.l}
                                                    type={e.t}
                                                    // original passes the lowercase `autocomplete` prop; forwarded via spread to preserve it.
                                                    {...{ autocomplete: e.ac }}
                                                    id={e.l}
                                                    disabled={loading || !!e.disabled}
                                                    onClick={() =>
                                                        setEdit({
                                                            [editingKey]: { ...editing[editingKey], [e.v]: `` },
                                                        })
                                                    }
                                                    fullWidth
                                                    InputProps={{
                                                        sx: {
                                                            background: `#FFF`,
                                                            marginTop: `5px`,
                                                        },
                                                    }}
                                                    sx={{
                                                        mb: 3,
                                                        maxWidth: `unset`,
                                                        ...(e.hidden ? { display: `none` } : {}),
                                                        input: { fontWeight: 500 },
                                                    }}
                                                    currentValue={editing[editingKey][e.v]}
                                                    helperText={errors[e.v]}
                                                    error={!!errors[e.v]}
                                                    onChange={v =>
                                                        setEdit({
                                                            [editingKey]: {
                                                                ...editing[editingKey],
                                                                [e.v]: e.visualFilter
                                                                    ? e.visualFilter(v.target.value)
                                                                    : v.target.value,
                                                            },
                                                        })
                                                    }
                                                />
                                            )}
                                            {fieldRules[editingKey].text && (
                                                <Paragraph
                                                    sx={{ my: 0, color: theme.palette.account.accent, fontSize: 16 }}
                                                >
                                                    {fieldRules[editingKey].text}
                                                </Paragraph>
                                            )}
                                        </FormControl>
                                    ))}
                                </Box>
                                <ModalButtonWrap>
                                    <SecondaryButton
                                        disabled={loading}
                                        size="small"
                                        variant="contained"
                                        onClick={() => {
                                            setEdit({});
                                            setErrors({});
                                        }}
                                        sx={{
                                            border: `1px solid ${theme.palette.account.accent}`,
                                            color: theme.palette.account.accent,
                                            background: `#FFF`,
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            "&:hover": {
                                                color: `#FFF`,
                                            },
                                        }}
                                    >
                                        Cancel
                                    </SecondaryButton>
                                    <Button
                                        disabled={loading}
                                        size="small"
                                        variant="contained"
                                        onClick={saveInfo}
                                        sx={{
                                            background: buttonGradient(theme),
                                            padding: `4px 8px`,
                                            minWidth: `unset`,
                                            borderRadius: 2,
                                            "&.Mui-disabled": {
                                                background: `#999 !important`,
                                                opacity: 0.5,
                                            },
                                        }}
                                    >
                                        {fieldRules[editingKey].btnText || `Save`}
                                    </Button>
                                </ModalButtonWrap>
                            </ModalBox>
                        </Card>
                    </AccountModal>
                )}
            </AccountWrap>
        </Block>
    );
};

export default MyAccount;
